import React, { useState, useRef } from 'react';
import Layout from '../../../components/layout';
import { motion } from "framer-motion";
import { ArrowRight, Check, Loader2, Upload } from "lucide-react";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useSearchParams } from 'react-router-dom';
import impact from "./impact.png";
import { csf_req } from '../../../../Config/config';

/* Small "i" beside a field label, explaining it on hover or keyboard focus.
A filled badge rather than a stroked icon - it stays crisp at this size. */
const FieldHint = ({ text }) => (
  <span className="group relative inline-flex items-center">
    <span
      tabIndex={0}
      role="img"
      aria-label={text}
      className="flex h-[16px] w-[16px] cursor-help select-none items-center justify-center rounded-full bg-[#bc8b20] font-serif text-[11px] italic leading-none text-white shadow-sm ring-1 ring-[#bc8b20]/20 transition-colors duration-150 hover:bg-[#a07924] focus:outline-none focus:ring-2 focus:ring-[#c59c40] focus:ring-offset-1"
    >
      i
    </span>
    <span
      role="tooltip"
      className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 w-60 -translate-x-1/2 rounded-lg bg-gray-900 px-3 py-2 text-xs font-normal normal-case leading-snug text-white shadow-xl opacity-0 invisible transition-all duration-150 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100"
    >
      {text}
      <span className="absolute left-1/2 top-full -mt-1 h-2 w-2 -translate-x-1/2 rotate-45 bg-gray-900" />
    </span>
  </span>
);

const FundingRequestFormInner = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);

  // Links can preselect who is filling the form, e.g. /compseekfund?type=advisor
  const [searchParams] = useSearchParams();
  const requestedType = searchParams.get('type');
  const presetUserType = requestedType === 'advisor' || requestedType === 'entrepreneur'
    ? requestedType
    : '';

  const [formData, setFormData] = useState({
    user_type: presetUserType,
    name: '',
    designation: '',
    registered_name: '',
    cin: '',
    country: '',
    city: '',
    industry: '',
    sector: '',
    website: '',
    amount_sought: '',
    revenue: '',
    email: '',
    mobile: '',
    linkedin: '',
    previously_funded: '',
    investors: '',
    comment: ''
  });
  
  const [files, setFiles] = useState({
    pitch_doc: null,
    logo: null
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  /* Advisors file on a client's company behalf and may not have its registration
  details to hand, so those fields stay optional for them. Entrepreneurs are filing
  for their own company, where they are still mandatory. */
  const isAdvisor = formData.user_type === 'advisor';
  const isCompanyDetailRequired = !isAdvisor;

  const handleFileChange = (e) => {
    setFiles(prev => ({
      ...prev,
      [e.target.name]: e.target.files[0]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Dismiss the previous result so a new attempt never shows a stale success banner
    setSuccess(false);

    if (!executeRecaptcha) {
      setError('reCAPTCHA not ready, please try again.');
      return;
    }

    if (!formData.user_type) {
      setError('Please select whether you are an Entrepreneur or an Advisor.');
      return;
    }

    // Required field validation
    const requiredFields = [
      'name', 'registered_name', 'amount_sought', 'email', 'mobile',
      ...(isCompanyDetailRequired ? ['cin', 'website'] : [])
    ];
    
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0 || !files.pitch_doc) {
      setError(`Please fill in all required fields: ${missingFields.join(', ')}${!files.pitch_doc ? ', pitch deck' : ''}`);
      return;
    }

    setLoading(true);
    setError('');

    let recaptchaToken;
    try {
      recaptchaToken = await executeRecaptcha('funding_request');
    } catch {
      setLoading(false);
      setError('reCAPTCHA check failed. Please try again.');
      return;
    }

    try {
      const formDataToSend = new FormData();
      
      // Add all text fields to FormData
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });
      
      // Add files
      if (files.pitch_doc) {
        formDataToSend.append('pitch_doc', files.pitch_doc);
      }
      
      if (files.logo) {
        formDataToSend.append('logo', files.logo);
      }
      
      // Add recaptcha token
      formDataToSend.append('recaptcha', recaptchaToken);

      const response = await fetch(csf_req, {
        method: 'POST',
        body: formDataToSend
      });

      if (!response.ok) {
        const responseText = await response.text();
        console.error('Error response:', responseText);
        throw new Error('Failed to submit form');
      }

      const data = await response.json();
      
      if (data.success) {
        setSuccess(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        // Clear form
        setFormData({
          name: '',
          designation: '',
          registered_name: '',
          cin: '',
          country: '',
          city: '',
          industry: '',
          sector: '',
          website: '',
          amount_sought: '',
          revenue: '',
          email: '',
          mobile: '',
          linkedin: '',
          previously_funded: 'no',
          investors: '',
          comment: ''
        });
        setFiles({
          pitch_doc: null,
          logo: null
        });
      } else {
        throw new Error(data.message || 'Submission failed');
      }
      
    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fields grouped by topic so each section reads as a short, self-contained step.
  const formSections = [
    {
      title: 'Your Details',
      fields: [
        { name: 'name', type: 'text', label: 'Your Name', required: true },
        { name: 'designation', type: 'text', label: formData.user_type === 'advisor' ? 'Firm Name' : 'Designation', required: false },
        { name: 'email', type: 'email', label: 'Email ID', required: true },
        { name: 'mobile', type: 'text', label: 'Mobile No.', required: true },
        { name: 'linkedin', type: 'url', label: 'Business/Personal LinkedIn URL', required: false },
      ],
    },
    {
      title: 'Company Details',
      fields: [
        {
          name: 'registered_name', type: 'text', label: 'Registered Name', required: true,
          hint: isAdvisor ? 'Enter a project name if you prefer not to disclose the company name' : null
        },
        { name: 'cin', type: 'text', label: 'CIN', required: isCompanyDetailRequired },
        { name: 'revenue', type: 'number', label: 'Revenue (in INR Cr)', required: false },
        { name: 'website', type: 'url', label: 'Website URL', required: isCompanyDetailRequired },
        { name: 'industry', type: 'text', label: 'Industry', required: false },
        { name: 'sector', type: 'text', label: 'Sector', required: false },
        { name: 'country', type: 'text', label: 'Country', required: false },
        { name: 'city', type: 'text', label: 'City', required: false },
      ],
    },
  ];

  const financialFormFields = [
    { name: 'amount_sought', type: 'number', label: 'Amount Sought (in INR Cr)', required: true },
  ];

  const inputClass = "block w-full h-11 rounded-md border-gray-300 shadow-sm focus:border-[#c59c40] focus:ring-[#c59c40] sm:text-sm px-4 border";
  const labelClass = "block text-sm font-medium text-gray-700";
  const sectionHeadingClass = "text-[15px] font-semibold text-[#8a6516] bg-[#FDF7E9] border-l-4 border-[#BC8B20] rounded-r-md px-4 py-2.5 mb-6";

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="relative h-48 mb-12">
            <div 
                className="absolute inset-0 bg-cover bg-center"
                style={{ 
                backgroundImage: `url(${impact})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
                }}
            >
                <div className="absolute inset-0 bg-black/20"></div>
            </div>
            
            <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4"
            >
                <h1 className="text-5xl font-bold text-[#efefef] mb-6 relative group">
                <span className="relative">
                Company Seeking Funding
                <span className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-[#c59c40] to-[#a07924] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></span>
                </span>
                </h1>
            </motion.div>
        </div>
        <div className="container mx-auto px-4 pb-16">
          {success ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-6xl mx-auto bg-white rounded-xl shadow-lg py-12 px-6 mb-8"
            >
              <div className="text-center">
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-green-50">
                  <Check className="h-7 w-7 text-green-600" />
                </div>
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                  Thank You for Your Submission!
                </h2>
                <p className="text-gray-700">
                  Your fundraise request has been submitted successfully. Our team will review and list it shortly.
                </p>
              </div>
            </motion.div>
          ) : (
          <>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="max-w-6xl mx-auto mb-8"
          >
            <p className="text-base text-gray-700 leading-relaxed text-left mb-5">
              Fill out the form with the necessary details and upload your pitch deck. Once submitted,
              we&rsquo;ll feature your request to our network of subscribers, including PE-VC investors,
              family offices, angel networks, investment banks, and advisors.
            </p>
            <h2 className="text-xl font-base text-gray-700 mb-1 text-left">
              Please complete the following details to proceed.
            </h2>
            <p className="text-sm text-gray-500 text-left">
              Only the fields marked * are required &mdash; the rest help us understand your business better.
            </p>
            {/* <p className="text-base text-gray-700 mb-4 leading-relaxed text-center">
            Please complete the following details to proceed.
            </p> */}
            {/* <h3 className="text-sm text-center text-gray-600 mb-6">
              Fields marked with * are required.
            </h3> */}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="max-w-6xl mx-auto bg-white rounded-xl shadow-lg py-10 px-8 mb-8"
          >
            <form onSubmit={handleSubmit} className="space-y-9" id="directoryform">
              {/* Who is filling the form - decides how the request is routed */}
              <div className="flex flex-wrap items-center gap-x-2 gap-y-2 h-11">
                <span className={labelClass}>Are you an</span>
                <label htmlFor="user_type_entrepreneur" className="text-sm font-medium text-gray-700 cursor-pointer">
                  Entrepreneur
                </label>
                <input
                  type="radio"
                  id="user_type_entrepreneur"
                  name="user_type"
                  value="entrepreneur"
                  checked={formData.user_type === 'entrepreneur'}
                  onChange={handleChange}
                  required
                  className="focus:ring-[#c59c40] h-4 w-4 text-[#c59c40] border-gray-300"
                />
                <span className="text-sm font-medium text-gray-700">or</span>
                <label htmlFor="user_type_advisor" className="text-sm font-medium text-gray-700 cursor-pointer">
                  Advisor
                </label>
                <input
                  type="radio"
                  id="user_type_advisor"
                  name="user_type"
                  value="advisor"
                  checked={formData.user_type === 'advisor'}
                  onChange={handleChange}
                  required
                  className="focus:ring-[#c59c40] h-4 w-4 text-[#c59c40] border-gray-300"
                />
                <span className="text-sm font-medium text-gray-700">*</span>
              </div>

              {/* Grouped sections: Your Details, Company Details */}
              {formSections.map((section) => (
                <div key={section.title}>
                  <h3 className={sectionHeadingClass}>{section.title}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5">
                    {section.fields.map((field) => (
                      <div key={field.name} className="space-y-1.5">
                        <label htmlFor={field.name} className={`${labelClass} flex items-center gap-1`}>
                          <span>{field.label}{field.required && ' *'}</span>
                          {field.hint && <FieldHint text={field.hint} />}
                        </label>
                        <input
                          type={field.type}
                          name={field.name}
                          id={field.name}
                          required={field.required}
                          value={formData[field.name]}
                          onChange={handleChange}
                          className={inputClass}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Funding Details */}
              <div>
                <h3 className={sectionHeadingClass}>Funding Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5">
                  {financialFormFields.map((field) => (
                    <div key={field.name} className="space-y-1.5">
                      <label htmlFor={field.name} className={`${labelClass} flex items-center gap-1`}>
                        <span>{field.label}{field.required && ' *'}</span>
                        {field.hint && <FieldHint text={field.hint} />}
                      </label>
                      <input
                        type={field.type}
                        name={field.name}
                        id={field.name}
                        required={field.required}
                        value={formData[field.name]}
                        onChange={handleChange}
                        className={inputClass}
                      />
                    </div>
                  ))}

                  {/* Funding Status */}
                  <div className="space-y-1.5">
                    <label className={labelClass}>
                      Previously funded? *
                    </label>
                    <div className="flex gap-6 h-11 items-center">
                      <div className="flex items-center">
                        <input
                          type="radio"
                          id="previously_funded_no"
                          name="previously_funded"
                          value="no"
                          checked={formData.previously_funded === 'no'}
                          onChange={handleChange}
                          className="focus:ring-[#c59c40] h-4 w-4 text-[#c59c40] border-gray-300"
                        />
                        <label htmlFor="previously_funded_no" className="ml-2 block text-sm font-medium text-gray-700">
                          No
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="radio"
                          id="previously_funded_yes"
                          name="previously_funded"
                          value="yes"
                          checked={formData.previously_funded === 'yes'}
                          onChange={handleChange}
                          className="focus:ring-[#c59c40] h-4 w-4 text-[#c59c40] border-gray-300"
                        />
                        <label htmlFor="previously_funded_yes" className="ml-2 block text-sm font-medium text-gray-700">
                          Yes
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Conditional Investors Field - sits in the grid so the layout does not jump */}
                  {formData.previously_funded === 'yes' && (
                    <div className="space-y-1.5">
                      <label htmlFor="investors" className={labelClass}>
                        Names of Investors (comma separated)
                      </label>
                      <input
                        type="text"
                        name="investors"
                        id="investors"
                        value={formData.investors}
                        onChange={handleChange}
                        className={inputClass}
                      />
                    </div>
                  )}
                </div>

                {/* Pitch deck and logo sit in this section - no separate Documents heading */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5 mt-6">
                  {/* Pitch Deck Upload */}
                  <div className="space-y-1.5">
                    <label className={labelClass}>
                      Pitch Deck *
                    </label>
                    <div className="flex items-center h-11">
                      <input
                        type="file"
                        name="pitch_doc"
                        id="pitch_doc"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        accept=".pdf,.ppt,.pptx,.doc,.docx"
                        className="sr-only"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="inline-flex items-center px-5 h-11 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#c59c40]"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Choose File
                      </button>
                      <span className="ml-3 text-sm text-gray-500 truncate max-w-xs">
                        {files.pitch_doc ? files.pitch_doc.name : 'No file chosen'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      Note: The file size must not exceed 10MB.
                    </p>
                  </div>
                  {/* Logo Upload */}
                  <div className="space-y-1.5">
                    <label className={labelClass}>
                      Brand Logo Image
                    </label>
                    <div className="flex items-center h-11">
                      <input
                        type="file"
                        name="logo"
                        id="logo"
                        ref={logoInputRef}
                        onChange={handleFileChange}
                        accept="image/*"
                        className="sr-only"
                      />
                      <button
                        type="button"
                        onClick={() => logoInputRef.current?.click()}
                        className="inline-flex items-center px-5 h-11 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#c59c40]"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Choose File
                      </button>
                      <span className="ml-3 text-sm text-gray-500 truncate max-w-xs">
                        {files.logo ? files.logo.name : 'No file chosen'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      Note: The file size must not exceed 2MB.
                    </p>
                  </div>
                </div>

                {/* Additional Comment */}
                <div className="mt-5 space-y-1.5">
                  <label htmlFor="comment" className={labelClass}>
                    Comment
                  </label>
                  <textarea
                    name="comment"
                    id="comment"
                    value={formData.comment}
                    onChange={handleChange}
                    rows={4}
                    className="block w-full lg:w-2/3 rounded-md border-gray-300 shadow-sm focus:border-[#c59c40] focus:ring-[#c59c40] sm:text-sm px-4 py-3 border"
                  />
                </div>
              </div>

              

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <p className="text-red-600 text-sm text-center">{error}</p>
                </div>
              )}

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  id="nextbtn"
                  disabled={loading}
                  className="w-full max-w-[11rem] mx-auto flex justify-center items-center py-4 px-6 border border-transparent rounded-md shadow-md text-base font-medium text-white bg-[#bc8b20] hover:bg-[#d6b15f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#c59c40] transition-colors duration-200"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Submit <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </button>
              </div>

            </form>
          </motion.div>
          </>
          )}
        </div>
      </div>
    </Layout>
  );
};

const FundingRequestForm = () => (
  <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY}>
    <FundingRequestFormInner />
  </GoogleReCaptchaProvider>
);

export default FundingRequestForm;